import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionMenu from '../action-buttons/ActionButtons';
import Loader from '../loader/Loader';
import PagedTable from '../sorted-table/PagedTable';
import { filtersType, InventoryLocationState } from '../../pages/inventory/Inventory';
import { Animal } from '../../types/interfaces/animal.interfaces';
import { PagedResponse, StandardParams } from '../../types/interfaces';
import { ViewMode } from '../../types/enums/viewMode.enum';
import { getAllAccountAnimals } from '../../api/animalsApi';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { showToast } from '../../services/toast.service';
import './animalTableItem.scss';

type Props = {
  accountId?: number;
  filters: filtersType;
  filterPreset?: filtersType;
  viewType: ViewMode;
  onPagingChange: (paging: { offset: number; total: number }) => void;
};

const PAGE_SIZE = 20;

const AnimalTableItem: FC<Props> = ({ accountId, filters, filterPreset, viewType, onPagingChange }): JSX.Element => {
  const navigate = useNavigate();

  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const callNumber: MutableRefObject<number> = useRef(0);
  const hasAllResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef(false);
  const animalsCountRef: MutableRefObject<number> = useRef(0);

  const filterKey = [filters.type.value, filters.species.value, filters.breed.value, filters.sort.value].join('|');

  const buildAccountAnimalParams = (params: StandardParams = {}) => ({
    include: params.include ?? 'Breed.Species, Attachments',
    limit: params.limit ?? PAGE_SIZE,
    offset: params.offset ?? 0,
    sort: filters.sort.value ?? params.sort ?? 'createdDatetime',
    searchString: filters.search || undefined,
    breedId: filters.breed.value ?? undefined,
    speciesId: filters.species.value ?? undefined,
    specimenTypeId: filters.type.value ?? undefined,
  });

  const syncPagingOffset = (requestOffset: number, data: Animal[] | PagedResponse<Animal>) => {
    if (Array.isArray(data)) {
      const loaded = requestOffset + data.length;
      onPagingChange({ offset: loaded, total: loaded });
      return data;
    }
    onPagingChange({
      offset: Math.min(requestOffset + data.result.length, data.total),
      total: data.total,
    });
    return data.result;
  };

  const loadMobileData = async (currentOffset: number) => {
    if (!accountId || hasAllResults.current) return;

    callNumber.current++;
    const callNum = callNumber.current;

    try {
      setLoading(true);
      const response = await getAllAccountAnimals(
        accountId,
        buildAccountAnimalParams({ offset: currentOffset, limit: PAGE_SIZE }),
      );

      if (callNum !== callNumber.current) return;

      const result = syncPagingOffset(currentOffset, response.data);
      setAnimals(prev => (currentOffset === 0 ? result : [...prev, ...result]));
      hasAllResults.current = result.length === 0;
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      if (callNum === callNumber.current) {
        setLoading(false);
        isLoadingAdditionalPage.current = false;
      }
    }
  };

  useEffect(() => {
    if (window.innerWidth > 768) {
      setLoading(false);
      return;
    }

    hasAllResults.current = false;
    setLoading(true);
    setAnimals([]);
    setOffset(0);
    void loadMobileData(0);
  }, [accountId, filters]);

  useEffect(() => {
    if (window.innerWidth > 768 || offset === 0) return;
    void loadMobileData(offset);
  }, [offset]);

  useEffect(() => {
    animalsCountRef.current = animals.length;
    isLoadingAdditionalPage.current = false;
  }, [animals]);

  useEffect(() => {
    if (window.innerWidth > 768) return;

    const handleScroll = () => {
      if (animalsCountRef.current === 0 || hasAllResults.current) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const position = scrollHeight - scrollTop;
      const atBottom = position - clientHeight <= clientHeight;

      if (atBottom && !isLoadingAdditionalPage.current) {
        isLoadingAdditionalPage.current = true;
        setOffset(prev => prev + PAGE_SIZE);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleView = (animalId: number | undefined) => {
    if (!animalId) return;

    const listState: InventoryLocationState = { filterPreset: filterPreset ?? filters, viewType };
    navigate(ROUTE_PATHS.APP_INVENTORY + '/' + animalId, { state: listState });
  };
  const renderActionMenu = (animalId: number | undefined) => (
    <ActionMenu
      actionButtons={[
        {
          name: BUTTON_CONSTANTS.VIEW_DETAIL,
          action: () => handleView(animalId),
        },
      ]}
    />
  );

  return (
    <div className="animal-table">
      <div className="desk-animal-table">
        <PagedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_NAME },
            { displayName: TABLE_HEADER_CONSTANTS.UNITS_OWNED },
            { displayName: TABLE_HEADER_CONSTANTS.SIRE },
            { displayName: TABLE_HEADER_CONSTANTS.DAM },
            { displayName: TABLE_HEADER_CONSTANTS.DNA },
            { displayName: TABLE_HEADER_CONSTANTS.ACTION },
          ]}
          sortBy="createdDatetime"
          include="Breed.Species, Attachments"
          filter={filterKey}
          searchString={filters.search || undefined}
          getData={params => getAllAccountAnimals(accountId as number, buildAccountAnimalParams(params))}
          height={500}
          pageSize={PAGE_SIZE}
          scrollBufferThickness={100}
          noResultsMessage={LABEL_CONSTANTS.NO_INVENTORY}
          onPagingChange={onPagingChange}
          buildRow={animal => [
            animal.name ?? '--',
            animal.quantityOwned ?? '--',
            animal.sire ?? '--',
            animal.dam ?? '--',
            animal.dna ?? '--',
            renderActionMenu(animal.animalId),
          ]}
        />
      </div>

      {/* Mobile View */}
      <div className="xs-animal-table">
        {!loading && animals.length === 0 ? (
          <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
        ) : (
          animals.map(animal => (
            <div className="card max-width" key={animal.animalId}>
              <div className="animal-card">
                <div className="animal-content">
                  <div className="animal-row">
                    <div className="animal-cell">
                      <label>{TABLE_HEADER_CONSTANTS.ANIMAL_NAME}: </label>
                      <label>{animal.name ?? '--'}</label>
                    </div>
                    <div className="animal-cell">
                      <label>{TABLE_HEADER_CONSTANTS.UNITS_OWNED}: </label>
                      <label>{animal.quantityOwned ?? '--'}</label>
                    </div>
                    <div className="animal-cell">
                      <label>{TABLE_HEADER_CONSTANTS.DAM}: </label>
                      <label>{animal.dam ?? '--'}</label>
                    </div>
                  </div>

                  <div className="animal-row">
                    <div className="animal-cell">
                      <label>{TABLE_HEADER_CONSTANTS.SIRE}: </label>
                      <label>{animal.sire ?? '--'}</label>
                    </div>
                    <div className="animal-cell">
                      <label>{TABLE_HEADER_CONSTANTS.DNA}: </label>
                      <label>{animal.dna ?? '--'}</label>
                    </div>
                  </div>
                </div>

                <div className="action-button">{renderActionMenu(animal.animalId)}</div>
              </div>
            </div>
          ))
        )}
        {loading && <Loader addedSpace loaderSize="small" />}
      </div>
    </div>
  );
};

export default AnimalTableItem;
